import React, { useState, useEffect } from 'react';
import { Document, Packer, Paragraph, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import axios from '../utils/api';
import DatePicker from 'react-datepicker';
import moment from 'moment-timezone';
import '../styles/UploadFile.css';
import "react-datepicker/dist/react-datepicker.css";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // matches Django FILE_UPLOAD_MAX_MEMORY_SIZE
const MAX_TOTAL_SIZE = 100 * 1024 * 1024;

const formatMb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const UploadFile = () => {
  const [files, setFiles] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sol_number, setSol_number] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailsText, setEmailsText] = useState('');
  const [sending, setSending] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sendType, setSendType] = useState('now');
  const [scheduledTime, setScheduledTime] = useState(null);
  const [marylandTime, setMarylandTime] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const updateMarylandTime = () => {
      const estTime = moment().tz("America/New_York").format("MMMM Do YYYY, h:mm:ss A");
      setMarylandTime(estTime);
    };
    updateMarylandTime();
    const interval = setInterval(updateMarylandTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const exportToDoc = async () => {
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({ text: "Solicitation Summary", heading: HeadingLevel.HEADING_2 }),
            new Paragraph({ text: analysis.step_1_summary || '', spacing: { after: 300 } }),
            new Paragraph({ text: "Email Subject", heading: HeadingLevel.HEADING_2 }),
            new Paragraph({ text: subject || analysis.step_2_subject || '', spacing: { after: 300 } }),
            new Paragraph({ text: "Email Body", heading: HeadingLevel.HEADING_2 }),
            ...message.split('\n').map(line => new Paragraph({ text: line })),
            new Paragraph({ text: "Step 3: Contact List", heading: HeadingLevel.HEADING_2, spacing: { before: 400 } }),
            ...analysis.step_3_contacts.split('\n').map(line => new Paragraph({ text: line })),
          ],
        },
      ],
    });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${sol_number}.docx`);
  };

  const addFiles = (incoming) => {
    const rejected = [];
    const accepted = [];
    let runningTotal = files.reduce((sum, file) => sum + file.size, 0);

    incoming.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name} (${formatMb(file.size)}) — max ${formatMb(MAX_FILE_SIZE)} per file`);
        return;
      }
      if (runningTotal + file.size > MAX_TOTAL_SIZE) {
        rejected.push(`${file.name} would exceed the ${formatMb(MAX_TOTAL_SIZE)} total upload limit`);
        return;
      }
      accepted.push(file);
      runningTotal += file.size;
    });

    if (rejected.length) {
      alert(`These files were not added and were not sent to the server:\n\n${rejected.join('\n')}`);
    }
    if (accepted.length) {
      setFiles((prevFiles) => [...prevFiles, ...accepted]);
    }
  };

  const filesWithinLimit = (list) => {
    const oversized = list.filter((file) => file.size > MAX_FILE_SIZE);
    const total = list.reduce((sum, file) => sum + file.size, 0);
    if (oversized.length) {
      alert(
        `These files are too large (max ${formatMb(MAX_FILE_SIZE)} each):\n\n` +
          oversized.map((file) => `${file.name} (${formatMb(file.size)})`).join('\n')
      );
      return false;
    }
    if (total > MAX_TOTAL_SIZE) {
      alert(`Selected files total ${formatMb(total)}. Maximum combined upload size is ${formatMb(MAX_TOTAL_SIZE)}.`);
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) return alert("Please select files.");
    if (!filesWithinLimit(files)) return;
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    setAnalyzing(true);
    try {
      const res = await axios.post('/auth/file/analyze/', formData, {
        headers: { 'Content-Type': undefined },
      });
      setAnalysis(res.data);
      setMessage(res.data.step_2_body);
      setSubject(res.data.step_2_subject);
      const extractedEmails = res.data.step_3_contacts.match(/[\w.+-]+@[\w-]+\.[\w.-]+/g);
      setEmails(extractedEmails || []);
      setEmailsText((extractedEmails || []).join('\n'));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.response?.data?.detail || "Error analyzing files.");
    }
    setAnalyzing(false);
  };

  const handleBroadcast = async () => {
    if (!message || !sol_number.trim()) {
      alert("Message and solicitation number are required.");
      return;
    }
    if (!filesWithinLimit(files)) return;

    const formData = new FormData();
    formData.append("message", message);
    formData.append("emails", JSON.stringify(emails));
    formData.append("subject", subject);
    formData.append("sol_number", sol_number);
    files.forEach(file => formData.append("attachments", file));

    if (sendType === 'schedule') {
      if (!scheduledTime) {
        alert("Please select a scheduled time.");
        return;
      }
      formData.append("scheduled_time", moment(scheduledTime).tz("America/New_York").format());
    }

    setSending(true);
    try {
      const endpoint = sendType === 'now' ? '/auth/file/broadcast/' : '/auth/file/schedule_broadcast/';
      await axios.post(endpoint, formData, {
        headers: { 'Content-Type': undefined },
      });
      alert(sendType === 'now' ? "Message sent!" : "Message scheduled!");
      setFiles([]);
      setAnalysis(null);
      setMessage('');
      setSubject('');
      setSol_number('');
      setEmails([]);
      setEmailsText('');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || err.response?.data?.detail || "Failed to send/schedule email.");
    }
    setSending(false);
  };

  return (
    <div className="upload-page">
      <div className="page-header">
        <h1>Upload & Analyze Files</h1>
        <p>Upload documents for AI analysis and generate personalized email content</p>
      </div>

      {!analysis ? (
        <div className="upload-section">
          <div 
            className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="upload-icon">
              <i className="fas fa-cloud-upload-alt"></i>
            </div>
            <h3>Drop files here or click to browse</h3>
            <p>PDF, Word, images, and more. Maximum {formatMb(MAX_FILE_SIZE)} per file, {formatMb(MAX_TOTAL_SIZE)} total.</p>
            <input
              type="file"
              multiple
              className="file-input"
              onChange={handleFileChange}
              id="file-upload"
            />
            <label htmlFor="file-upload" className="btn btn-primary">
              <i className="fas fa-folder-open"></i>
              Select Files
            </label>
          </div>

          {files.length > 0 && (
            <div className="files-preview">
              <h4>Selected Files</h4>
              <div className="files-list">
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <i className="fas fa-file-alt"></i>
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{formatMb(file.size)}</span>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="btn btn-success" 
                onClick={handleUpload}
                disabled={analyzing}
              >
                {analyzing ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Analyzing Files...
                  </>
                ) : (
                  <>
                    <i className="fas fa-brain"></i>
                    Analyze Files with AI
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="analysis-results">
          <div className="results-header">
            <h2>AI Analysis Results</h2>
            <button className="btn btn-outline-primary" onClick={exportToDoc}>
              <i className="fas fa-download"></i>
              Export to Word
            </button>
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-file-text"></i>
              Step 1: Solicitation Summary
            </label>
            <div className="summary-box">{analysis.step_1_summary}</div>
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-tag"></i>
              Step 2: Solicitation Number
            </label>
            <input
              type="text"
              className="form-control"
              value={sol_number}
              placeholder="Enter solicitation number"
              onChange={(e) => setSol_number(e.target.value)}
            />
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-envelope"></i>
              Step 2: Email Subject
            </label>
            <input
              type="text"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-edit"></i>
              Step 2: Email Template
            </label>
            <textarea
              className="form-control"
              rows="12"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-users"></i>
              Step 3: Contact List
            </label>
            <textarea
              className="form-control"
              rows="6"
              value={analysis.step_3_contacts}
              readOnly
            />
          </div>

          <div className="result-section">
            <label className="section-label">
              <i className="fas fa-at"></i>
              Step 3: Contact Emails
            </label>
            <textarea
              className="form-control"
              rows="6"
              value={emailsText}
              onChange={(e) => {
                const rawText = e.target.value;
                setEmailsText(rawText);
                setEmails(rawText.split('\n').map(email => email.trim()).filter(email => email));
              }}
            />
          </div>

          <div className="result-section">
            <div className="section-header-with-action">
              <label className="section-label">
                <i className="fas fa-paperclip"></i>
                Email Attachments
              </label>
              <label htmlFor="add-file-input" className="btn btn-sm btn-outline-primary">
                <i className="fas fa-plus"></i>
                Add Files
              </label>
              <input
                type="file"
                multiple
                id="add-file-input"
                className="file-input-hidden"
                onChange={handleFileChange}
              />
            </div>
            
            {files.length > 0 ? (
              <div className="attachments-list">
                {files.map((file, index) => (
                  <div key={index} className="attachment-item">
                    <i className="fas fa-file-alt"></i>
                    <span className="attachment-name">{file.name}</span>
                    <span className="attachment-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      className="attachment-remove"
                      onClick={() => handleRemoveFile(index)}
                      title="Remove file"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-attachments">
                <i className="fas fa-inbox"></i>
                <p>No files attached</p>
              </div>
            )}
          </div>

          <div className="time-info">
            <i className="fas fa-clock"></i>
            <span>Maryland (EST) Time: <strong>{marylandTime}</strong></span>
          </div>

          <div className="send-options">
            <div className="send-type-selector">
              <button
                className={`btn ${sendType === 'now' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setSendType('now')}
              >
                Send Now
              </button>
              <button
                className={`btn ${sendType === 'schedule' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setSendType('schedule')}
              >
                Schedule Send
              </button>
            </div>

            {sendType === 'schedule' && (
              <div className="schedule-section">
                <label>Choose Maryland Time to Send</label>
                <DatePicker
                  className="form-control"
                  selected={scheduledTime}
                  onChange={(date) => setScheduledTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  placeholderText="Select date and time"
                />
              </div>
            )}
          </div>

          <div className="warning-box">
            <i className="fas fa-exclamation-triangle"></i>
            <span>Please review all files expected as attachments before sending</span>
          </div>

          <button
            className="btn btn-success btn-large"
            onClick={handleBroadcast}
            disabled={sending}
          >
            {sending ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                {sendType === 'now' ? 'Send Now' : 'Schedule Send'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadFile;
